import { useLayoutEffect, useRef } from 'react'
import gsap from 'gsap'
import { revealOnScroll, staggerChildren } from '../animations/gsapAnimations'

/* gsap.context() scopes every selector to the element and, more importantly,
   reverts every tween and ScrollTrigger on unmount. Without the revert,
   React 19's StrictMode double-invoke leaves two of each behind in dev.

   The setup callback is captured on first render, so it must not close over
   changing values — pass what varies through `deps` instead, which re-runs
   the setup (and reverts the previous context) when it changes. */

/** Run arbitrary GSAP setup scoped to an element, cleaned up automatically. */
export function useGsapContext<T extends HTMLElement = HTMLDivElement>(
  /* The return is deliberately untyped: most setups end in a concise arrow
     that hands back the timeline they built, which is ignored. A setup may
     instead return a teardown function, for effects gsap.context cannot
     revert on its own — SplitText rewrites the DOM, and reverting tweens
     leaves that markup behind. */
  setup: (scope: T) => unknown,
  /* Pass a dependency when the elements to animate arrive after mount — a
     grid fed by the data layer has no children to stagger on first paint. */
  deps: unknown[] = [],
) {
  const ref = useRef<T>(null)
  const setupRef = useRef(setup)

  useLayoutEffect(() => {
    const scope = ref.current
    if (!scope) return

    let teardown: (() => void) | undefined
    const ctx = gsap.context(() => {
      const result = setupRef.current(scope)
      if (typeof result === 'function') teardown = result as () => void
    }, scope)

    return () => {
      teardown?.()
      ctx.revert()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return ref
}

/** The common case: fade and rise this element in when it is scrolled to. */
export function useGsapReveal<T extends HTMLElement = HTMLDivElement>(
  options: Parameters<typeof revealOnScroll>[1] = {},
) {
  return useGsapContext<T>((scope) => {
    revealOnScroll(scope, options)
  })
}

/** Reveal a container's children in sequence — grids, card rows, lists. */
export function useGsapStagger<T extends HTMLElement = HTMLDivElement>(
  selector: string,
  options: Parameters<typeof staggerChildren>[2] = {},
  deps: unknown[] = [],
) {
  return useGsapContext<T>((scope) => {
    staggerChildren(scope, selector, options)
  }, deps)
}
